export interface ApiErrorEnvelope {
  error?: {
    code?: string;
    message?: string;
    fieldErrors?: Record<string, readonly string[]>;
    correlationId?: string;
  };
}

export class ApiRequestError extends Error {
  public readonly status: number;
  public readonly code: string;
  public readonly fieldErrors: Record<string, readonly string[]>;
  public readonly correlationId?: string;

  public constructor(
    status: number,
    code: string,
    message: string,
    fieldErrors: Record<string, readonly string[]> = {},
    correlationId?: string,
  ) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = code;
    this.fieldErrors = fieldErrors;
    if (correlationId !== undefined) this.correlationId = correlationId;
  }
}

export interface ApiRequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  csrfToken?: string | undefined;
  supportAccessId?: string | undefined;
  signal?: AbortSignal | undefined;
}

export async function apiRequest<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
  const headers = new Headers({ Accept: 'application/json' });
  if (options.body !== undefined) headers.set('Content-Type', 'application/json');
  if (options.csrfToken !== undefined) headers.set('X-CSRF-Token', options.csrfToken);
  if (options.supportAccessId !== undefined) {
    headers.set('X-Support-Access-ID', options.supportAccessId);
  }

  const init: RequestInit = {
    method: options.method ?? 'GET',
    headers,
    credentials: 'same-origin',
  };
  if (options.body !== undefined) init.body = JSON.stringify(options.body);
  if (options.signal !== undefined) init.signal = options.signal;

  const response = await fetch(path, init);
  if (response.status === 204) return undefined as T;

  const contentType = response.headers.get('content-type') ?? '';
  const payload: unknown = contentType.includes('application/json')
    ? await response.json()
    : await response.text();

  if (!response.ok) {
    const envelope = payload as ApiErrorEnvelope;
    const error = envelope.error;
    throw new ApiRequestError(
      response.status,
      error?.code ?? 'REQUEST_FAILED',
      error?.message ?? `The request failed with status ${response.status}.`,
      error?.fieldErrors ?? {},
      error?.correlationId,
    );
  }

  return payload as T;
}

export function errorMessage(error: unknown): string {
  if (error instanceof ApiRequestError) {
    const fieldMessages = Object.values(error.fieldErrors).flat();
    return fieldMessages.length > 0 ? `${error.message} ${fieldMessages.join(' ')}` : error.message;
  }
  if (error instanceof Error) return error.message;
  return 'An unexpected error occurred.';
}

export async function apiFormDataRequest<T>(
  path: string,
  formData: FormData,
  options: Pick<ApiRequestOptions, 'csrfToken' | 'supportAccessId' | 'signal'> = {},
): Promise<T> {
  const headers = new Headers({ Accept: 'application/json' });
  if (options.csrfToken !== undefined) headers.set('X-CSRF-Token', options.csrfToken);
  if (options.supportAccessId !== undefined) {
    headers.set('X-Support-Access-ID', options.supportAccessId);
  }
  const init: RequestInit = {
    method: 'POST',
    headers,
    credentials: 'same-origin',
    body: formData,
  };
  if (options.signal !== undefined) init.signal = options.signal;

  const response = await fetch(path, init);
  const contentType = response.headers.get('content-type') ?? '';
  const payload: unknown = contentType.includes('application/json')
    ? await response.json()
    : await response.text();
  if (!response.ok) {
    const envelope = payload as ApiErrorEnvelope;
    const error = envelope.error;
    throw new ApiRequestError(
      response.status,
      error?.code ?? 'REQUEST_FAILED',
      error?.message ?? `The request failed with status ${response.status}.`,
      error?.fieldErrors ?? {},
      error?.correlationId,
    );
  }
  return payload as T;
}
